import { NO, YES } from "uicore-ts"


/**
 * RHProcurementWinnerStatus
 *
 * Production has NO procurement-level single-winner field. Winners are recorded
 * per part in `partWinnerOfferIds` (part._id -> winning RHProcurementPartOffer._id),
 * set by `SetProcurementPartWinner` on the server.
 *
 * The mock-up distinguished two separate flags:
 *   - winnerTenderId      -> a full winner for the whole procurement
 *   - partWinnerTenderIds -> winners chosen for some of the parts
 *
 * This helper maps that distinction onto production's per-part data so every
 * table can read winner state from one source of truth:
 *   - "full"    = every part of the procurement has a winner assigned
 *                 (for a single-part procurement this is simply "the one part is awarded")
 *   - "partial" = at least one — but not all — parts have a winner
 *   - "none"    = no part has a winner
 *
 * "full" and "partial" are mutually exclusive, which is exactly what the procurer
 * status chain ("Olete valinud võitja" vs "Olete valinud osale võitja") and the
 * tenderer-side winner badges need.
 */
export type RHProcurementWinnerKind = "none" | "partial" | "full"


/**
 * The raw procurement fields every status/lifecycle/winner derivation in this
 * folder reads from. One shared shape so callers don't juggle several
 * structurally-identical "Source" types for the same data.
 */
export type RHProcurementStatusSource = {
    status?: string
    deadlineDate?: Date | string
    parts?: { _id?: unknown }[]
    partWinnerOfferIds?: Record<string, string>
    finalizedPartIds?: string[]
}


export class RHProcurementWinnerStatus {

    static winnerKind(procurement: RHProcurementStatusSource | undefined): RHProcurementWinnerKind {
        const parts = procurement?.parts ?? []
        if (parts.length === 0) {
            return "none"
        }
        const awardedPartCount = this._awardedPartCount(procurement)
        if (awardedPartCount === 0) {
            return "none"
        }
        if (awardedPartCount === parts.length) {
            return "full"
        }
        return "partial"
    }


    static hasFullWinner(procurement: RHProcurementStatusSource | undefined): boolean {
        return this.winnerKind(procurement) === "full"
    }


    static hasPartialWinner(procurement: RHProcurementStatusSource | undefined): boolean {
        return this.winnerKind(procurement) === "partial"
    }


    static hasFinalizedWinningPart(procurement: RHProcurementStatusSource | undefined): boolean {
        const partWinnerOfferIds = procurement?.partWinnerOfferIds ?? {}
        const finalizedPartIds = procurement?.finalizedPartIds ?? []
        const hasFinalizedWinningPart = finalizedPartIds.some(
            partId => !!partWinnerOfferIds[partId]
        )
        if (hasFinalizedWinningPart) {
            return YES
        }

        // Compatibility for procurements finalized before per-part finalization
        // tracking was introduced. Their terminal status proves the winner was
        // finalized even though finalizedPartIds is absent.
        return finalizedPartIds.length === 0 &&
            procurement?.status === "evaluation-finalized" &&
            Object.keys(partWinnerOfferIds).length > 0
    }


    /**
     * Returns YES only when this specific offer holds the winning part offer for
     * EVERY part of the procurement (i.e. it won the whole procurement). Used for
     * the offerer-side "Olete võitja!" full-winner badge, which must NOT trigger
     * when the offerer won only some parts (reproduces the mock-up part-winner gap).
     */
    static offerIsFullWinner(
        procurement: RHProcurementStatusSource | undefined,
        offerPartOfferIds: Set<string>
    ): boolean {
        const parts = procurement?.parts ?? []
        if (parts.length === 0) {
            return NO
        }
        return parts.every(part => {
            const winningPartOfferId = this._winningPartOfferIdForPart(procurement, part)
            if (!winningPartOfferId) {
                return NO
            }
            return offerPartOfferIds.has(winningPartOfferId.toString())
        })
    }


    private static _awardedPartCount(procurement: RHProcurementStatusSource | undefined): number {
        const parts = procurement?.parts ?? []
        return parts.filter(part => !!this._winningPartOfferIdForPart(procurement, part)).length
    }


    private static _winningPartOfferIdForPart(
        procurement: RHProcurementStatusSource | undefined,
        part: { _id?: unknown }
    ): string | undefined {
        const partId = part?._id?.toString()
        if (!partId) {
            return undefined
        }
        return procurement?.partWinnerOfferIds?.[partId]
    }

}
